import type { AcceptQuoteInput } from '@chukua/common';
import { useState } from 'react';
import { ApiError } from '../../../../../shared/shared.api';
import { useToast } from '../../../../../shared/hooks/use-toast';
import { acceptQuote, rejectQuote, type JobDetail } from '../../../jobs.api';

/**
 * The accept/reject decision on a quote (gitaumoses4/chukua#34) — same `conflictError`/
 * `submitting`/409-reload shape as `use-job-credit-hold.ts`'s `decide`, kept separate from
 * `use-job-actions.ts` since it's a decision off `JobQuotePanel`, not one of `JobAction`'s
 * state-action buttons.
 */
export function useJobQuote(reload: () => Promise<void>) {
  const [conflictError, setConflictError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  async function accept(job: Pick<JobDetail, 'id' | 'number'>, input: AcceptQuoteInput): Promise<void> {
    setConflictError(null);
    setSubmitting(true);
    try {
      const result = await acceptQuote(job.id, input);
      await reload();
      toast.success(result.status === 'on_hold' ? `Job ${job.number} flagged for credit review — on hold` : `Job ${job.number} confirmed`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        await reload();
        setConflictError("This job's state changed since the page loaded — showing the latest.");
      } else {
        toast.error(`Could not accept job ${job.number}'s quote — try again.`);
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function reject(job: Pick<JobDetail, 'id' | 'number'>, reason: string): Promise<void> {
    setConflictError(null);
    setSubmitting(true);
    try {
      await rejectQuote(job.id, { reason });
      await reload();
      toast.success(`Job ${job.number}'s quote rejected — cancelled`);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        await reload();
        setConflictError("This job's state changed since the page loaded — showing the latest.");
      } else {
        toast.error(`Could not reject job ${job.number}'s quote — try again.`);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return { conflictError, submitting, accept, reject };
}
